// src/components/communs/LecteurEvaluation.jsx
import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { APP_CONFIG } from '../../config/appConfig';
import Evaluation from './Evaluation';
import { Loader2, AlertCircle } from 'lucide-react';

export default function LecteurEvaluation({
    evaluationId,
    etudiantId,
    onClose,
    onSubmitSuccess
}) {
    const { token } = useAuth();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [evaluation, setEvaluation] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState(null);
    const [lastSubmittedPayload, setLastSubmittedPayload] = useState(null);

    // ⭐ Clé de cycle de vie : force React à REMONTER <Evaluation/> à chaque
    // nouvelle ouverture depuis le parent (si celui-ci garde LecteurEvaluation
    // monté et change juste evaluationId). Combiné au reset interne du
    // composant, cela garantit qu'une évaluation démarre toujours par la
    // "porte d'entrée" (bouton Commencer).
    const openCycleRef = useRef(0);
    const previousEvalIdRef = useRef(null);
    if (previousEvalIdRef.current !== evaluationId) {
        previousEvalIdRef.current = evaluationId;
        openCycleRef.current += 1;
    }

    // ---------- Chargement de l'évaluation ----------
    useEffect(() => {
        let cancelled = false;
        const fetchEvaluation = async () => {
            if (!evaluationId || !token) return;
            setLoading(true);
            setError(null);
            try {
                const response = await fetch(
                    `${APP_CONFIG.BASE_URL}/api/v1/etudiant/evaluations/${evaluationId}?etudiantId=${etudiantId}`,
                    { headers: { 'Authorization': `Bearer ${token}` } }
                );
                if (!response.ok) throw new Error('Erreur chargement de l\'évaluation');
                const data = await response.json();
                if (!cancelled) setEvaluation(data);
            } catch (err) {
                if (!cancelled) setError(err.message);
            } finally {
                if (!cancelled) setLoading(false);
            }
        };
        fetchEvaluation();
        return () => { cancelled = true; };
    }, [evaluationId, token, etudiantId]);

    // ---------- Soumission ----------
    const handleSubmit = async (resultats) => {
        setSubmitting(true);
        setSubmitError(null);
        setLastSubmittedPayload(resultats);

        try {
            // ⭐ On utilise routingMode/targetId fournis par Evaluation pour
            // garantir que l'URL de POST /repondre correspond exactement à
            // celle de POST /demarrer (même clé Redis en entrée et en sortie).
            const mode = resultats?.mode || 'evaluation';
            const targetId = resultats?.targetId || evaluationId;

            const reponses = Object.entries(resultats?.answers || {}).map(([key, userAnswer]) => {
                const questionIndex = parseInt(key.split('-')[1], 10);
                if (userAnswer && typeof userAnswer === 'object' && !Array.isArray(userAnswer)) {
                    // APPARIE / CLASSE / CATEGORI : on envoie les valeurs dans l'ordre des clés
                    return {
                        questionIndex,
                        reponsesTexte: Object.values(userAnswer).map(String)
                    };
                }
                if (Array.isArray(userAnswer)) {
                    return { questionIndex, reponsesTexte: userAnswer.map(String) };
                }
                if (typeof userAnswer === 'number') {
                    return { questionIndex, reponseChoisie: userAnswer };
                }
                return { questionIndex, reponsesTexte: [String(userAnswer ?? '')] };
            });

            const url = mode === 'module'
                ? `${APP_CONFIG.BASE_URL}/api/v1/etudiant/modules/${targetId}/repondre?etudiantId=${etudiantId}`
                : `${APP_CONFIG.BASE_URL}/api/v1/etudiant/evaluations/${targetId}/repondre?etudiantId=${etudiantId}`;

            const response = await fetch(url, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(reponses)
            });

            if (!response.ok) {
                let detail = `HTTP ${response.status}`;
                try {
                    const errBody = await response.json();
                    if (errBody?.message || errBody?.error) detail = errBody.message || errBody.error;
                } catch { /* ignore */ }
                throw new Error(detail);
            }

            const serverResult = await response.json();
            console.log('Résultat évaluation (serveur) :', serverResult);

            setSubmitError(null);
            if (onSubmitSuccess) onSubmitSuccess();
        } catch (err) {
            console.error('Erreur soumission évaluation:', err);
            setSubmitError(err.message || 'Erreur inconnue');
        } finally {
            setSubmitting(false);
        }
    };

    const handleRetrySubmit = () => {
        if (lastSubmittedPayload) {
            handleSubmit(lastSubmittedPayload);
        }
    };

    // ---------- Rendu ----------
    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-[400px]">
                <Loader2 className="w-10 h-10 sm:w-12 sm:h-12 animate-spin text-blue-600" />
            </div>
        );
    }

    if (error) {
        return (
            <div className="flex items-center justify-center min-h-[400px]">
                <div className="text-center max-w-md px-4">
                    <AlertCircle className="w-12 h-12 sm:w-14 sm:h-14 text-red-500 mx-auto mb-4" />
                    <p className="text-sm sm:text-base text-gray-600">{error}</p>
                </div>
            </div>
        );
    }

    if (!evaluation) return null;

    const questions = evaluation.questions || [];

    return (
        <>
            {submitting && (
                <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40">
                    <div className="bg-white rounded-xl px-4 py-3 sm:px-6 sm:py-4 flex items-center gap-3 shadow-lg">
                        <Loader2 className="w-5 h-5 animate-spin text-blue-600" />
                        <span className="text-xs sm:text-sm text-gray-700">Envoi de vos réponses…</span>
                    </div>
                </div>
            )}
            <Evaluation
                // ⭐ Clé de cycle : remonte le composant à chaque nouvelle
                // ouverture d'évaluation → garantit le reset complet.
                key={`eval-${evaluationId}-${openCycleRef.current}`}
                module={{ id: evaluationId }}
                evaluationData={[{ titre: evaluation.titre, questions }]}
                formationTitre={evaluation.titre || 'Évaluation'}
                moduleOrdre={1}
                totalModules={1}
                onClose={onClose}
                isOpen={true}
                onComplete={() => {}}
                onSubmit={handleSubmit}
                onSubmitSuccess={onSubmitSuccess}
                evalType={evaluation.type || 'SOMMATIVE'}
                evaluationId={evaluationId}
                etudiantId={etudiantId}
                // ⭐ CHANGEMENT 4 : le token était manquant, ce qui faisait
                // silencieusement échouer /demarrer → 400 sur /repondre.
                token={token}
                // ⭐ Permet à l'UI d'afficher un bandeau + bouton « Réessayer »
                // dans la page de résumé sans quitter le modal.
                submitError={submitError}
                submitLoading={submitting}
                onRetrySubmit={handleRetrySubmit}
            />
        </>
    );
}